import React from 'react';
import { Lead } from '../types';
import { stepLabel, MANUSCRIPT_LABELS, SALES_STATUS_LABELS, SALES_STATUS_BADGE_STYLES, SortField, SortDir } from '../stepLabels';
import { relativeTime } from '../utils';

interface LeadsTableProps {
  leads: Lead[];
  onOpenContact: (lead: Lead) => void;
  sortBy: SortField;
  sortDir: SortDir;
  onSortChange: (field: SortField) => void;
}

interface SortableHeaderProps {
  field: SortField;
  label: string;
  sortBy: SortField;
  sortDir: SortDir;
  onSortChange: (field: SortField) => void;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({ field, label, sortBy, sortDir, onSortChange }) => {
  const active = sortBy === field;
  return (
    <th className="px-6 py-3">
      <button
        onClick={() => onSortChange(field)}
        className={`flex items-center gap-1 hover:text-gray-700 ${active ? 'text-gray-700' : ''}`}
      >
        {label}
        <span className="text-[10px]">{active ? (sortDir === 'asc' ? '▲' : '▼') : ''}</span>
      </button>
    </th>
  );
};

const LeadsTable: React.FC<LeadsTableProps> = ({ leads, onOpenContact, sortBy, sortDir, onSortChange }) => {
  if (leads.length === 0) {
    return <div className="py-16 text-center text-sm text-gray-400">No leads match these filters.</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
            <SortableHeader field="name" label="Name" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} />
            <SortableHeader field="phoneNumber" label="Phone" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} />
            <th className="px-6 py-3">Book</th>
            <th className="px-6 py-3">Stage</th>
            <th className="px-6 py-3">Manuscript</th>
            <th className="px-6 py-3">Contact Status</th>
            <SortableHeader field="salesUpdatedAt" label="Last Contacted" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} />
            <SortableHeader field="updatedAt" label="Book Last Updated" sortBy={sortBy} sortDir={sortDir} onSortChange={onSortChange} />
            <th className="px-6 py-3"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {leads.map((lead) => (
            <tr key={lead.bookId} className="hover:bg-gray-50">
              <td className="px-6 py-3 font-medium text-gray-900">{lead.userName || '—'}</td>
              <td className="px-6 py-3 text-gray-600">{lead.phoneNumber || '—'}</td>
              <td className="px-6 py-3 text-gray-600">{lead.bookTitle || 'Untitled'}</td>
              <td className="px-6 py-3 text-gray-600">{stepLabel(lead.currentStep)}</td>
              <td className="px-6 py-3 text-gray-600">{MANUSCRIPT_LABELS[lead.manuscriptType] || lead.manuscriptType}</td>
              <td className="px-6 py-3">
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${SALES_STATUS_BADGE_STYLES[lead.salesStatus]}`}>
                  {SALES_STATUS_LABELS[lead.salesStatus]}
                </span>
              </td>
              <td className="px-6 py-3 text-gray-400">{relativeTime(lead.salesUpdatedAt)}</td>
              <td className="px-6 py-3 text-gray-400">{relativeTime(lead.updatedAt)}</td>
              <td className="px-6 py-3 text-right">
                <button
                  onClick={() => onOpenContact(lead)}
                  disabled={!lead.phoneNumber}
                  title={!lead.phoneNumber ? 'No phone number on file for this lead' : undefined}
                  className="text-blue-600 hover:text-blue-800 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Log Contact
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default LeadsTable;
